/**
 * types.ts — [CONTRACT] TypeScript mirror of the health-snapshot DTO.
 *
 * This is the frontend half of the FE/BE seam defined in
 *   services/backoffice/app/contracts.py        (Pydantic, source of truth)
 *   services/backoffice/tests/fixtures/contract.example.json   (shared fixture)
 *
 * It MUST match contracts.py field-for-field. The drift guard lives in
 * `types.test.ts`, which validates the SAME committed fixture the backend
 * round-trips — if a field changes on either side, both move together or a
 * test fails (story E0-BO-S3).
 *
 * The `SOURCE_STATUSES` / `SOURCE_KEYS` const arrays are the single runtime
 * source for their union types (`as const` → `typeof[number]`), so the enum
 * domain is bound to the type, not duplicated by hand.
 */

// --- Status enum (mirrors contracts.SourceStatus) --------------------------
// Order matters for nothing at runtime, but `unknown` is a first-class value,
// distinct from `down` (used for timeout / no-signal).
export const SOURCE_STATUSES = ['ok', 'degraded', 'down', 'unknown'] as const;
export type SourceStatus = (typeof SOURCE_STATUSES)[number];

// --- Source keys (mirrors contracts.SourceKey) -----------------------------
export const SOURCE_KEYS = ['containers', 'pipeline_flow', 'dependencies'] as const;
export type SourceKey = (typeof SOURCE_KEYS)[number];

// --- DTOs (mirror contracts.py models) -------------------------------------

/** One labelled measurement within a HealthItem. Mirrors contracts.Metric. */
export interface Metric {
  label: string;
  value: string | number | boolean | null;
  unit?: string | null;
}

/** One card within a source group (e.g. 'postgres'). Mirrors contracts.HealthItem. */
export interface HealthItem {
  key: string;
  label: string;
  status: SourceStatus;
  reason?: string | null;
  metrics: Metric[];
}

/** One collector's output = one card group. Mirrors contracts.SourceSnapshot. */
export interface SourceSnapshot {
  source: SourceKey;
  status: SourceStatus;
  reason?: string | null;
  items: HealthItem[];
  /** ISO-8601 timestamp (Pydantic datetime serialised to string over JSON). */
  collected_at: string;
}

/** The unified snapshot. Mirrors contracts.HealthOverview. */
export interface HealthOverview {
  overall: SourceStatus;
  sources: SourceSnapshot[];
  /** ISO-8601 timestamp. */
  generated_at: string;
  cache_age_seconds: number;
}

// --- Runtime guards (used by the dashboard + the drift test) ---------------

export const isSourceStatus = (value: unknown): value is SourceStatus =>
  typeof value === 'string' && (SOURCE_STATUSES as readonly string[]).includes(value);

export const isSourceKey = (value: unknown): value is SourceKey =>
  typeof value === 'string' && (SOURCE_KEYS as readonly string[]).includes(value);

// ===========================================================================
// [CONTRACT] LEADS PANEL — TS mirror of the Leads DTOs (story E0-BO-LS4).
//
// Mirrors the Pydantic source of truth in
//   services/backoffice/app/contracts.py        (LEADS section)
// and the shared parity fixture
//   services/backoffice/tests/fixtures/contract.leads.example.json
//
// Field-for-field with contracts.py. The drift guard is `types.leads.test.ts`,
// which validates the SAME committed fixture the Python shape test round-trips.
// This is the HARD GATE for LS5–LS8 — both halves move together or a test fails.
//
// Const arrays (`as const` → `typeof[number]`) are the single runtime source
// for the union types, so each enum domain is bound to its type, not duplicated.
// ===========================================================================

// --- The frozen 13-value lead state set (mirrors contracts.LeadStatus) ------
// Order matches packages/shared/state_transition_service LEAD_STATES + 0031.
export const LEAD_STATUSES = [
  'new',
  'pulled',
  'contacted',
  'engaged',
  'collecting',
  'docs_complete',
  'handed_off',
  'closed_known',
  'closed_unknown',
  'opted_out',
  'undeliverable',
  'consent_unverified',
  'quarantine',
] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

// --- Derived funnel buckets (mirrors contracts.FunnelStage) -----------------
export const FUNNEL_STAGES = [
  'new',
  'in_conversation',
  'collecting',
  'handed_off',
  'closed',
  'exception',
] as const;
export type FunnelStage = (typeof FUNNEL_STAGES)[number];

// --- Audit actor (mirrors contracts.LeadActor) ------------------------------
export const LEAD_ACTORS = ['system', 'operator', 'lead'] as const;
export type LeadActor = (typeof LEAD_ACTORS)[number];

// --- Search kinds — hashes + id only, never raw PII (mirrors LeadSearchKind) -
export const LEAD_SEARCH_KINDS = ['phone_hash', 'cpf_hash', 'lead_id'] as const;
export type LeadSearchKind = (typeof LEAD_SEARCH_KINDS)[number];

/** One row in the paginated lead list. Mirrors contracts.LeadListItem. */
export interface LeadListItem {
  id: string;
  status: LeadStatus;
  product_code: string | null;
  channel: string;
  /** clients.name when joinable, else null → render "—". */
  display_name: string | null;
  /** ISO-8601 timestamp. */
  created_at: string;
  /** ISO-8601 timestamp. */
  status_updated_at: string;
  funnel_stage: FunnelStage;
}

/** Keyset-paginated wrapper. Mirrors contracts.LeadListEnvelope. */
export interface LeadListEnvelope {
  items: LeadListItem[];
  /** Opaque keyset cursor; null = last page. */
  next_cursor: string | null;
  page_limit: number;
  /** APPROXIMATE only; null when not computed. Never an exact COUNT. */
  approx_total: number | null;
  /** ISO-8601 timestamp. */
  generated_at: string;
}

/** Tagged search input — already hashed client-side (Q5). Mirrors LeadSearchTerm. */
export interface LeadSearchTerm {
  kind: LeadSearchKind;
  value: string;
}

/** The list request contract. Mirrors contracts.LeadFilterParams. */
export interface LeadFilterParams {
  status: LeadStatus[];
  product_code?: string | null;
  channel?: string | null;
  tenant_id?: string | null;
  /** ISO date (YYYY-MM-DD). */
  created_from?: string | null;
  created_to?: string | null;
  status_from?: string | null;
  status_to?: string | null;
  search?: LeadSearchTerm | null;
  cursor?: string | null;
  limit: number;
}

/** One row of the append-only audit timeline. Mirrors LeadStateHistoryEntry. */
export interface LeadStateHistoryEntry {
  from_status: LeadStatus | null;
  to_status: LeadStatus;
  event_name: string;
  actor: LeadActor;
  /** ISO-8601 timestamp. */
  occurred_at: string;
  note: string | null;
}

/** Shared lead↔conversation link contract. Mirrors contracts.ConversationLink. */
export interface ConversationLink {
  lead_id: string;
  has_conversation: boolean;
  deep_link: string;
}

/** Full operator-facing lead record (PII-minimised). Mirrors contracts.LeadDetail. */
export interface LeadDetail {
  id: string;
  status: LeadStatus;
  funnel_stage: FunnelStage;
  product_code: string | null;
  channel: string;
  display_name: string | null;
  /** Masked phone, e.g. "+55 11 ****-3616"; never raw (Q3). */
  phone_e164_masked: string | null;
  /** Masked CPF; raw CPF never surfaced in v1 (Q3). */
  cpf_masked: string | null;
  lgpd_consent: boolean;
  /** ISO-8601 timestamp; null when consent not yet captured. */
  lgpd_consent_at: string | null;
  created_at: string;
  updated_at: string;
  status_updated_at: string;
  /** PII-scrubbed subset only (Q3); never the raw blob. */
  normalized_data: Record<string, unknown> | null;
  state_history: LeadStateHistoryEntry[];
  conversation_link: ConversationLink | null;
  /** Legal next statuses (Q1); empty when the contingent write is off. */
  allowed_transitions: LeadStatus[];
}

/** One fact Mari captured for a lead (E12-S5.3). Mirrors contracts.LeadFact. */
export interface LeadFact {
  key: string;
  label: string;
  value: string;
  format: string;
  builtin: boolean;
  /** ISO-8601 timestamp. */
  captured_at: string;
}

/** `GET /api/leads/{id}/facts` response. Server-ordered; never re-sort client-side. */
export interface LeadFacts {
  lead_id: string;
  facts: LeadFact[];
}

/** Operator transition request (Q1). `actor` is set server-side, never here. */
export interface LeadTransitionRequest {
  to_status: LeadStatus;
  note?: string | null;
}

/** The resulting lead state + the newly-appended history entry. */
export interface LeadTransitionResponse {
  lead: LeadDetail;
  applied_entry: LeadStateHistoryEntry;
}

// --- Manual lead creation (LS12). Mirrors contracts.ManualLeadCreateRequest --
// and the sibling response/conflict/options shapes in contracts.py.

/** Lawful-basis vocabulary (mirrors contracts.LawfulBasis / lead-intake's enum). */
export const LAWFUL_BASES = ['consent', 'legitimate_interest', 'contract_preliminary'] as const;
export type LawfulBasis = (typeof LAWFUL_BASES)[number];

/**
 * The operator's "New lead" form submission. NO `actor`/`operator` field — the
 * BFF reads identity from the authenticated principal and rejects one supplied
 * here (422, `extra='forbid'`). `source_note` is required only when
 * `lawful_basis === 'legitimate_interest'`; the cap (500 chars, D3) is enforced
 * here as a convenience and authoritatively on the server.
 */
export interface ManualLeadCreateRequest {
  phone: string;
  display_name: string;
  tenant_id: string;
  product_code: string;
  lawful_basis: LawfulBasis;
  source_note?: string | null;
}

/** `201` — mirrors contracts.ManualLeadCreateResponse. */
export interface ManualLeadCreateResponse {
  lead_id: string;
  status: string;
}

/** `409` body — mirrors contracts.ManualLeadConflict. */
export interface ManualLeadConflict {
  detail: string;
  existing_lead_id: string;
}

export interface TenantOption {
  id: string;
  name: string;
}

export interface ProductOption {
  code: string;
  name: string;
}

export interface LawfulBasisOption {
  value: LawfulBasis;
  label: string;
}

/** `GET /api/leads/form-options` — mirrors contracts.LeadFormOptions. */
export interface LeadFormOptions {
  tenants: TenantOption[];
  products: ProductOption[];
  lawful_bases: LawfulBasisOption[];
}

export const SOURCE_NOTE_MAX_LENGTH = 500;

// --- Runtime guards (used by the views + the drift test) --------------------

export const isLeadStatus = (value: unknown): value is LeadStatus =>
  typeof value === 'string' && (LEAD_STATUSES as readonly string[]).includes(value);

export const isFunnelStage = (value: unknown): value is FunnelStage =>
  typeof value === 'string' && (FUNNEL_STAGES as readonly string[]).includes(value);

export const isLeadActor = (value: unknown): value is LeadActor =>
  typeof value === 'string' && (LEAD_ACTORS as readonly string[]).includes(value);

export const isLeadSearchKind = (value: unknown): value is LeadSearchKind =>
  typeof value === 'string' && (LEAD_SEARCH_KINDS as readonly string[]).includes(value);

// ===========================================================================
// [CONTRACT B / CONV2] CONVERSATIONS PANEL — TS mirror of the Conversations
// DTOs (story E0-BO-CONV2). The HARD GATE for CONV3–CONV8.
//
// Mirrors the Pydantic source of truth in
//   services/backoffice/app/contracts_conversations.py
// and the shared parity fixture
//   services/backoffice/tests/fixtures/contract.conversations.example.json
//
// Field-for-field with contracts_conversations.py. The drift guard is
// `types.conversations.test.ts`, which validates the SAME committed fixture the
// Python shape test round-trips. Both halves move together or a test fails.
//
// `FunnelStage` / `FUNNEL_STAGES` are REUSED from the Leads section above
// (ADR-057 shared vocabulary) — not redefined.
//
// PII: the only human-readable phone is `phone_masked`. `thread_id` is an
// OPAQUE, URL-safe handle encoding tenant:phone for control addressing — the
// client treats it as opaque, never parses a raw phone from it. The cross-panel
// identifier is `lead.id` (carried by ConversationLeadLink), not tenant:phone.
//
// NAMING (CONV1 trap): the count field is `inbound_message_count` — a handled-
// INBOUND count (increments even on paused threads; may double-count on
// redelivery), DISTINCT from the LangGraph graph state `message_count` (the
// AI-turn count). Never name it bare `message_count` or `turn_count` here.
// ===========================================================================

// --- Small frozen enums (mirror contracts_conversations.py) -----------------
export const CONSENT_STATES = ['granted', 'declined', 'unknown'] as const;
export type ConsentState = (typeof CONSENT_STATES)[number];

// `unknown` = delivery health not yet assessed (outbox seam lands with CONV4;
// CONV3 must not assert a sourced state). See ADR-058 Addendum B.
export const DELIVERY_STATUSES = ['ok', 'pending', 'stuck', 'error', 'unknown'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

// Named controls. `reset` is intentionally absent — never a control name (gated
// via `reset_availability`). `requeue` IS a name but is gated in v1 (ADR-058
// Addendum C): surfaced via `requeue_availability`, dropped from
// `available_controls`. Only pause|resume take effect today.
export const CONTROL_NAMES = ['pause', 'resume', 'requeue'] as const;
export type ControlName = (typeof CONTROL_NAMES)[number];

// Honesty flag on the list envelope (mirrors contracts.DetailCompleteness).
export const DETAIL_COMPLETENESS = ['single_replica', 'degraded_multi_replica'] as const;
export type DetailCompleteness = (typeof DETAIL_COMPLETENESS)[number];

// Control audit actor — always 'operator', set server-side (mirrors ControlActor).
export type ControlActor = 'operator';

/** Whether a checkpointer-dependent feature is available. Mirrors contracts.FeatureAvailability. */
export interface FeatureAvailability {
  /** false on the current (non-durable) backend. */
  available: boolean;
  reason: string;
  unblocked_by: string;
}

/** One message in the gated transcript. Mirrors contracts.TranscriptMessage. */
export interface TranscriptMessage {
  direction: 'inbound' | 'outbound';
  text: string;
  /** ISO-8601 timestamp — checkpoint-snapshot approximation. */
  sent_at: string;
  /** Exact per-turn send time (ISO-8601 UTC). Null for legacy turns; fall back to `sent_at`. */
  at: string | null;
}

/** The gated transcript pane state. Mirrors contracts.TranscriptState. */
export interface TranscriptState {
  availability: FeatureAvailability;
  /** Empty until the durable conversation store lands. */
  messages: TranscriptMessage[];
}

/** Conversation→Lead deep link (cross-panel, by lead.id). Mirrors contracts.ConversationLeadLink. */
export interface ConversationLeadLink {
  lead_id: string;
  /** Backoffice route, e.g. "/leads?lead_id=<uuid>". */
  deep_link: string;
}

/** One row in the paginated conversation list. Mirrors contracts.ConversationListItem. */
export interface ConversationListItem {
  /** OPAQUE URL-safe handle encoding tenant:phone; addressing key only, never a raw phone. */
  thread_id: string;
  /** Masked phone, e.g. "+55 11 ****-0001"; never raw. */
  phone_masked: string;
  channel: string;
  waba_number: string | null;
  /** Composed by the BFF from capital-service; null when no lead or upstream down. */
  funnel_stage: FunnelStage | null;
  /** Agent open script step id; null when unknown. */
  script_step: string | null;
  /** Lead name from capital-service (personal data, ADR-058 Addendum D.4); null when unknown. */
  lead_display_name: string | null;
  /** Handled-INBOUND count (NOT the AI-turn graph-state count). */
  inbound_message_count: number;
  /** ISO-8601 timestamp. */
  last_activity_at: string;
  consent_state: ConsentState;
  delivery_status: DeliveryStatus;
  is_paused: boolean;
  /** Cross-panel link target; null when the thread maps to no lead. */
  lead_id: string | null;
}

/** Keyset-paginated wrapper. Mirrors contracts.ConversationListEnvelope. */
export interface ConversationListEnvelope {
  items: ConversationListItem[];
  /** Opaque keyset over last_activity; null = last page. */
  next_cursor: string | null;
  page_limit: number;
  /** APPROXIMATE only; null when not computed. */
  approx_total: number | null;
  /** ISO-8601 timestamp. */
  generated_at: string;
  detail_completeness: DetailCompleteness;
}

/** Full operator-facing conversation record (PII-minimised). Mirrors contracts.ConversationDetail. */
export interface ConversationDetail {
  thread_id: string;
  phone_masked: string;
  channel: string;
  waba_number: string | null;
  funnel_stage: FunnelStage | null;
  script_step: string | null;
  inbound_message_count: number;
  /** ISO-8601 timestamp. */
  last_activity_at: string;
  /** ISO-8601 timestamp; null when not derivable from a cold checkpoint. */
  started_at: string | null;
  consent_state: ConsentState;
  delivery_status: DeliveryStatus;
  /** Rows eligible for requeue. */
  stuck_outbound_count: number;
  is_paused: boolean;
  lead_link: ConversationLeadLink | null;
  /** pause|resume only (ADR-058 Addendum C) — NOT reset, NOT requeue (both gated, via their *_availability). */
  available_controls: ControlName[];
  /** GATED — available=false today. */
  transcript: TranscriptState;
  /** GATED — shares the transcript (durable-checkpointer) dependency. */
  reset_availability: FeatureAvailability;
  /** GATED v1 (ADR-058 Addendum C) — DISTINCT dependency: requeue's target re-scope, NOT the checkpointer. */
  requeue_availability: FeatureAvailability;
}

/** Operator control request. `actor` is set server-side, never here. Mirrors contracts.ControlRequest. */
export interface ControlRequest {
  action: 'pause' | 'resume' | 'requeue';
}

/** The outcome of a control action. Mirrors contracts.ConversationControlResult. */
export interface ConversationControlResult {
  thread_id: string;
  action: ControlName;
  /** Resulting state delta, e.g. { is_paused: true } or { requeued_count: 3 }. */
  new_state: Record<string, unknown>;
  /** ISO-8601 timestamp. */
  performed_at: string;
  /** Always 'operator', set server-side; echoed for the UI. */
  actor: ControlActor;
}

// --- Combined "reset lead" action (CONV11c, ADR-079 D4/D5) -----------------
//
// Mirrors contracts_conversations.ResetLeadHalfResult / ResetLeadRequest /
// ResetLeadResponse. `POST /api/conversations/{thread}/reset-lead` is the
// sole operator-facing reset trigger, replacing the old gated per-thread
// reset control (CONV4/CONV8) — `reset_availability` above described THAT
// control's gate and is no longer consulted by the SPA's reset UI (see
// ConversationDetailPage). A funnel-half 404/409-conflict/502 surfaces as an
// HTTP error (404/502) or, for the 409 conflict case specifically, as this
// 200 body with `funnel_reset: FAILED` / `conversation_reset: SKIPPED` — the
// SPA renders every outcome off this body's two fields, never off HTTP status
// alone.

export const RESET_LEAD_HALF_RESULTS = ['succeeded', 'failed', 'skipped'] as const;
export type ResetLeadHalfResult = (typeof RESET_LEAD_HALF_RESULTS)[number];

/** Mirrors contracts_conversations.ResetLeadRequest. `reason` is optional (AC4). */
export interface ResetLeadRequest {
  lead_id: string;
  reason?: string | null;
}

/**
 * Per-half outcome of the combined reset (AC2). `success` is true only when
 * BOTH halves succeeded. Mirrors contracts_conversations.ResetLeadResponse.
 */
export interface ResetLeadResponse {
  success: boolean;
  funnel_reset: ResetLeadHalfResult;
  conversation_reset: ResetLeadHalfResult;
  funnel_error?: string | null;
  conversation_error?: string | null;
}

/**
 * One "Todas as mensagens" ledger row (Fowler's design doc, Slice 2). Mirrors
 * contracts_conversations.MessageRow — a pure passthrough of miv-engine's
 * message ledger. `body` is UNMASKED (Aron's 2026-09-17 ruling; forensic, not
 * conversational recall) — render as-is, never truncate. `source` and
 * `status` are deliberately free-form strings (not enums): slice 4 adds
 * `outbox_outbound` rows and new `status` values without a shape change.
 */
export interface MessageRow {
  direction: 'inbound' | 'outbound' | 'system';
  /** ISO-8601 UTC timestamp. */
  at: string;
  body: string;
  source: string;
  wamid: string | null;
  status: string;
  /**
   * Decision-ledger correlation handle (E0-BO-DECISION-LEDGER-MESSAGEROW-
   * TURN-ID) — matches a DecisionRow.turn_id for the same turn. `null` for
   * outbox_outbound and transcript rows (no turn-correlation field there).
   */
  turn_id: string | null;
}

/**
 * One "Auditoria" decision-ledger row (decision-ledger design spec §7.2,
 * slice 4). Mirrors contracts_conversations.DecisionRow — a pure passthrough
 * of miv-engine's decision ledger. `event` and `verdict` are deliberately
 * free-form strings (not enums): miv-engine grows the vocabulary without a
 * lock-step BFF release. `stage` is a closed union (DB-CHECKed upstream).
 */
export interface DecisionRow {
  turn_id: string;
  seq: number;
  /** ISO-8601 UTC timestamp. */
  at: string;
  stage: 'webhook' | 'loop' | 'dispatch';
  event: string;
  step_id: string | null;
  verdict: string | null;
  reason: string | null;
  /**
   * Verbatim passthrough. On a tool-call row: `payload.summary` is the
   * allow-listed business-layer read (`ok`, `error_code`, plus per-tool
   * fields) and the one to render; `payload.envelope` is the full untruncated
   * forensic tool result.
   */
  payload: Record<string, unknown>;
}

/**
 * MLflow trace list (Fowler's design doc, "MLflow design", Slice 6). Mirrors
 * `contracts_traces.py` — additive, no parity test (no existing TS mirror to
 * disturb; see that file's docstring). `attributes` values render verbatim,
 * pre-wrap, never truncated.
 */
export interface TraceSpan {
  name: string;
  kind: string;
  /** ISO-8601 timestamp. */
  started_at: string;
  duration_ms: number;
  status: string;
  attributes: Record<string, string>;
}

export interface TraceItem {
  trace_id: string;
  /** ISO-8601 timestamp. */
  started_at: string;
  duration_ms: number;
  status: string;
  spans: TraceSpan[];
}

export interface TraceListEnvelope {
  items: TraceItem[];
  total: number;
}

// --- Runtime guards (used by the views + the drift test) --------------------

export const isConsentState = (value: unknown): value is ConsentState =>
  typeof value === 'string' && (CONSENT_STATES as readonly string[]).includes(value);

export const isDeliveryStatus = (value: unknown): value is DeliveryStatus =>
  typeof value === 'string' && (DELIVERY_STATUSES as readonly string[]).includes(value);

export const isControlName = (value: unknown): value is ControlName =>
  typeof value === 'string' && (CONTROL_NAMES as readonly string[]).includes(value);

export const isResetLeadHalfResult = (value: unknown): value is ResetLeadHalfResult =>
  typeof value === 'string' && (RESET_LEAD_HALF_RESULTS as readonly string[]).includes(value);

export const isDetailCompleteness = (value: unknown): value is DetailCompleteness =>
  typeof value === 'string' && (DETAIL_COMPLETENESS as readonly string[]).includes(value);

// ===========================================================================
// [CONTRACT] SKILL CONFIG REGISTRY PANEL — mirrors the BFF's skill-registry
// admin routes (story E0-BO-CFG-SKILL-REGISTRY-ADMIN), verified 2026-08-01
// against `services/backoffice/app/contracts.py` and
// `services/capital-service/app/domain/skill_registry/` on branch
// feat/E0-S0.1-bo-cfg-registry-api (PR #534) — not derived from the brief.
//
// `graph_config_json` is opaque, READ-ONLY cargo — the server copies it from
// the base version on create and ignores anything the client sends; this UI
// never re-sends it. Every OTHER key on `prompt_template_json` this panel
// does not understand (e.g. `nodes`) is round-tripped untouched via the index
// signature on PromptTemplateJson, so editing grounding/product_facts/
// classifiers never clobbers a sibling map this UI is not scoped to touch.
// ===========================================================================

/** The three legal `fact_categories` values — never free text (guardrail). */
export const FACT_CATEGORIES = ['mandatory', 'optional', 'verbatim_value'] as const;
export type FactCategory = (typeof FACT_CATEGORIES)[number];

/**
 * One turn-intent grounding entry. Mirrors packages/shared GroundingEntry
 * (migration 0075 / ADR-074 D-1). `fact_categories` and
 * `mandatory_fact_patterns` are declared HERE, per grounding entry, keyed by
 * a `reference_facts` key WITHIN that entry — they describe that entry's own
 * facts, not `product_facts` (a sibling top-level map with no category/
 * pattern metadata of its own). The index signature preserves any field this
 * UI does not know about (e.g. one added to GroundingEntry after this panel
 * shipped) — the edit form must never reconstruct an entry from a named
 * field list, only spread-and-override the fields it deliberately edits.
 */
export interface GroundingEntry {
  instruction: string;
  reference_facts: Record<string, string | string[]>;
  anchor_template: string;
  mandatory_string_categories: string[];
  /**
   * reference_facts key -> category. Values constrained to FACT_CATEGORIES.
   * Absent on any row seeded before migration 0075 (ADR-074 D-1) — the
   * Pydantic `default_factory=dict` never ran against pre-existing JSONB, so
   * this key is genuinely missing, not empty. Every reader must guard it.
   */
  fact_categories?: Record<string, FactCategory>;
  /**
   * reference_facts key -> substring ALTERNATES — NOT regular expressions.
   * Same pre-0075 absence as `fact_categories` above.
   */
  mandatory_fact_patterns?: Record<string, string[]>;
  [key: string]: unknown;
}

/**
 * One classifier's prompt config. Only `system_prompt` is editable here — the
 * rest (model, temperature, max_tokens, expected_labels, ...) is opaque
 * cargo, round-tripped untouched.
 */
export interface ClassifierPromptConfig {
  system_prompt: string;
  [key: string]: unknown;
}

/**
 * The prompt_template_json blob. Only `grounding`, `product_facts`, and each
 * classifier's `system_prompt` are editable in this panel (per the story's
 * scope). Every other key (e.g. `nodes`) passes through untouched.
 */
export interface PromptTemplateJson {
  grounding: Record<string, GroundingEntry> | null;
  product_facts: Record<string, string> | null;
  classifiers: Record<string, ClassifierPromptConfig> | null;
  [key: string]: unknown;
}

/**
 * `GET /api/config/skills/keys` list item — a real (skill_name, product_code)
 * pair, backing the panel's lookup selectors. `product_code` is genuinely
 * nullable in the registry and must stay `null`, never coerced to `"null"`
 * or `""`, anywhere it flows through the client (story E0-BO-CFG-S3).
 */
export interface SkillKey {
  skill_name: string;
  product_code: string | null;
}

/**
 * One row in `GET /api/config/skills`. `agent_key` (ADR-082 D-6,
 * `docs/adr/ADR-082_...tool-enforced-gates.md`) is the row's agent persona,
 * derived server-side from `agent_config_json["agent_key"]`; `null` for a
 * pre-agentic (node-graph) row.
 */
export interface SkillVersionSummary {
  skill_name: string;
  config_version: string;
  is_active: boolean;
  /** ISO-8601 timestamp. */
  created_at: string;
  agent_key: string | null;
}

/** `GET /api/config/skills` envelope — paginated, filtered, sorted. */
export interface SkillVersionList {
  items: SkillVersionSummary[];
  total: number;
}

/**
 * `GET /api/config/skills/{skill_name}/versions/{config_version}`.
 * `agent_config_json` is READ-ONLY here — no edit path in this contract
 * (editor deferred to Aron, ADR-082).
 */
export interface SkillVersionDetail extends SkillVersionSummary {
  prompt_template_json: PromptTemplateJson;
  /** Never editable, never re-sent — the server owns it. Shown read-only. */
  graph_config_json: Record<string, unknown>;
  agent_config_json: Record<string, unknown> | null;
}

/**
 * One machine-readable save-time validation failure OR warning
 * ({field_path, reason}). `script_key`/`step_id` (ADR-084 D4, E12-S3a) are
 * present only on an `agent_config_json` issue — mirrors
 * `services/capital-service/app/api/skill_registry.py::ValidationIssueDTO`.
 */
export interface SkillVersionFieldError {
  field_path: string;
  reason: string;
  script_key?: string | null;
  step_id?: string | null;
}

/**
 * `POST /api/config/skills/{skill_name}/versions` request body. `tenant_id`
 * is deliberately ABSENT — the BFF route fills it server-side from
 * `BackofficeConfig.capital_service_tenant_id` and no longer accepts a
 * client-supplied value (E0-BO-CFG-S3 / PR #540).
 *
 * `agent_config_json` (ADR-084 D3, E12-S3a) is present only for a write to
 * an agentic row and requires the Entra `Admin` app role server-side.
 * `prompt_template_json` stays REQUIRED on every write, agentic included —
 * `CreateSkillVersionRequestBody` has no default for it (capital-service;
 * Ritchie's BFF mirror, PR 713, is unchanged) — an agentic save sends the
 * loaded row's `prompt_template_json` back byte-identical alongside
 * `agent_config_json`, never omits it.
 */
export interface CreateSkillVersionRequest {
  product_code: string;
  base_config_version: string;
  new_config_version: string;
  /** graph_config_json is deliberately absent — the server copies the base's. */
  prompt_template_json: PromptTemplateJson;
  agent_config_json?: AgentConfigDoc | null;
}

// ===========================================================================
// [CONTRACT] AGENTIC CONFIG DOC — TS mirror of ADR-084 D1's typed
// `agent_config_json`, `packages/shared/models/agent_config.py`. Pydantic v2,
// `extra="forbid"` there; this mirror carries the same field-for-field shape
// and the same `max_length` caps (AGENT_CONFIG_MAX_LENGTHS below), read from
// that module on 2026-09-22. No parity test yet (no committed fixture on
// either side) — matched against the module by hand this session; a drift
// guard is future work, same discipline the other [CONTRACT] blocks use once
// a shared fixture exists.
// ===========================================================================

export interface LockedStringRef {
  key: string;
  ref: string;
}

export interface Persona {
  name: string;
  voice_summary: string;
  disclosure_rule: string;
}

export interface Definition {
  agent_definition_key: string;
  version: number;
  tenant_id: string;
  persona: Persona;
  locked_strings: LockedStringRef[];
  model_tier: string;
}

/** Absent `text` → the `id` is rendered (today's behaviour, config_adapter.py:53-56). */
export interface BoundaryRule {
  id: string;
  text: string | null;
}

export interface Boundaries {
  boundaries_key: string;
  version: number;
  rules: BoundaryRule[];
}

/** Ruling 8 (PR 708): `text` is tenant-editable, Admin only, NO content check — deliberately outside the label/delimiter guards (D4). */
export interface Disclaimer {
  id: string;
  ref: string | null;
  text: string | null;
}

export const FACT_DEF_FORMATS = [
  'text',
  'number',
  'date',
  'yes_no',
  'enum',
  'regex',
  'referential',
] as const;
export type FactDefFormat = (typeof FACT_DEF_FORMATS)[number];

/**
 * Replaces the in-code `FACT_KEYS` / `FACT_VALUE_RULES` pair. A `builtin`
 * row's `key`/`format`/`pattern`/`values` are engineering-owned (D4 rule 5)
 * — the editor renders them read-only; `label`/`question_guidance`/order/
 * `required` stay editable on every row, builtin or not.
 */
export interface FactDef {
  key: string;
  label: string;
  question_guidance: string;
  format: FactDefFormat;
  pattern: string | null;
  values: string[] | null;
  description: string | null;
  required: boolean;
  builtin: boolean;
}

export interface Step {
  step_id: string;
  goal: string;
  enabled: boolean;
  /** Ordered — this IS the requested-information order (D1). */
  required_facts: string[];
  allowed_tools: string[];
  exit_when: string;
  /** Unchanged grammar — consumed by next_open_step only, not edited here. */
  advance_when: Record<string, unknown> | null;
  on_decline: string;
  on_ineligible: string;
  on_consent_not_classified: string;
  retry_guidance: string;
  /** S7 — lead status the agent drives to when this step opens; null/absent = none. */
  lead_status?: 'contacted' | 'engaged' | 'collecting' | 'docs_complete' | null;
}

export interface Script {
  product_script_key: string;
  kind: 'entry' | 'product';
  version: number;
  product_code: string | null;
  display_name: string;
  steps: Step[];
}

// ADR-085 D1 — `eligibility` / `document_checklist`, mirrored field-for-field
// from `packages/shared/models/vehicle_eligibility.py` and
// `document_checklist.py` (read 2026-09-22). `null` on a legacy row that
// predates the S1 migration (D5 rule 10 only requires both when the doc
// carries a car-equity product script) — the editor (S4) treats `null` as
// "not configured here", no client-side scaffold to create the section from
// scratch.

/** ADR-085 D5 rule 14 — the four fixed CGV-2 disqualification identifiers; a fifth value is rejected server-side. */
export const ELIGIBILITY_FAIL_REASONS = [
  'collateral_not_lien_free',
  'collateral_age_exceeded',
  'collateral_category_ineligible',
  'applicant_not_owner',
] as const;
export type EligibilityFailReason = (typeof ELIGIBILITY_FAIL_REASONS)[number];

/** ADR-085 D5 rule 13 — exactly these four keys, no more, no fewer (Aron's ruling 1: no add/remove control). */
export const ELIGIBILITY_QUESTION_KEYS = ['ownership', 'payoff', 'lien', 'category'] as const;

export interface EligibilityQuestionConfig {
  prompt: string;
  pass_label: string;
  options: Record<string, string[]>;
  fail_reasons: Record<string, EligibilityFailReason>;
  uncertain_labels: string[];
}

export interface VehicleEligibilityConfig {
  age_limit_years: number;
  weight_limit_tonnes: number;
  /** Bounded re-ask budget (ADR-085 ruling 1: fully editable, same as age_limit_years/weight_limit_tonnes). */
  reask_attempts: number;
  sequence: string[];
  questions: Record<string, EligibilityQuestionConfig>;
}

/** ADR-085 D5 rule 11 — the in-code document-type vocabulary (`app/core/vision/schemas/document_result.py`). */
export const DOCUMENT_TYPES = ['cnh', 'crlv', 'income_proof'] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export interface DocumentChecklistConfig {
  sequence: string[];
  types: Record<string, string>;
  request_templates: Record<string, string>;
}

export interface AgentConfigDoc {
  agent_key: string;
  schema_version: '1.0.0';
  definition: Definition;
  boundaries: Boundaries;
  cadence_instructions: string;
  scripts: Script[];
  tools: string[];
  facts: FactDef[];
  disclaimers: Disclaimer[];
  eligibility: VehicleEligibilityConfig | null;
  document_checklist: DocumentChecklistConfig | null;
}

/** D1's `max_length` caps — the client-side pre-check counters key off these; the server model is the authority (D4 rule 9). */
export const AGENT_CONFIG_MAX_LENGTHS = {
  personaName: 40,
  personaVoiceSummary: 600,
  personaDisclosureRule: 600,
  boundaryRuleText: 500,
  cadenceInstructions: 1500,
  factLabel: 60,
  factQuestionGuidance: 300,
  stepGoal: 600,
  stepExitWhen: 300,
  stepOnDecline: 400,
  stepOnIneligible: 400,
  stepOnConsentNotClassified: 400,
  stepRetryGuidance: 300,
  eligibilityPrompt: 400,
  eligibilityPassLabel: 60,
  eligibilityOptionToken: 60,
  checklistRequestTemplate: 400,
} as const;

/**
 * Trusts the server's D1a normaliser (ADR-084) for the values, but tolerates
 * legacy-shape rows that lack whole sections: absent arrays/objects are filled
 * with empty defaults so the editor opens instead of throwing on `.flatMap`.
 */
export const asAgentConfigDoc = (raw: Record<string, unknown>): AgentConfigDoc => {
  const doc = raw as unknown as Partial<AgentConfigDoc>;
  const definition = (doc.definition ?? {}) as Partial<Definition>;
  const boundaries = (doc.boundaries ?? {}) as Partial<Boundaries>;
  return {
    ...doc,
    definition: {
      ...definition,
      persona: definition.persona ?? { name: '', voice_summary: '', disclosure_rule: '' },
    },
    boundaries: { ...boundaries, rules: boundaries.rules ?? [] },
    cadence_instructions: doc.cadence_instructions ?? '',
    scripts: doc.scripts ?? [],
    tools: doc.tools ?? [],
    facts: doc.facts ?? [],
    disclaimers: doc.disclaimers ?? [],
  } as AgentConfigDoc;
};

/**
 * Legacy-shape row: the raw document lacks `scripts` or `definition.persona`.
 * The editor opens it (with filled defaults) but must not save it — the server
 * requires persona and would drop a stored legacy `script` (conversion is a
 * separate card).
 */
export const isLegacyShapeAgentConfig = (raw: Record<string, unknown>): boolean =>
  raw.scripts === undefined ||
  (raw.definition as { persona?: unknown } | undefined)?.persona === undefined;

/**
 * `201` — the newly created (inactive) version, plus any non-blocking
 * warnings. `warnings` is `{field_path, reason}[]` — the SAME shape as a 422
 * error, NOT a list of strings (e.g. a regex-looking mandatory_fact_patterns
 * alternate is accepted but warned on).
 */
export interface CreateSkillVersionResponse extends SkillVersionDetail {
  warnings: SkillVersionFieldError[];
}

/**
 * `POST .../versions/{config_version}/activate` request body. `tenant_id` is
 * absent — filled server-side from `BackofficeConfig.capital_service_tenant_id`
 * (E0-BO-CFG-S3 / PR #540).
 */
export interface ActivateSkillVersionRequest {
  product_code: string;
}

export const isFactCategory = (value: unknown): value is FactCategory =>
  typeof value === 'string' && (FACT_CATEGORIES as readonly string[]).includes(value);

// ===========================================================================
// [CONTRACT B / NUM3] NUMBERS & PRODUCTS PANEL — TS mirror of the Numbers
// DTOs (story E0-BO-NUM3). The HARD GATE for NUM4–NUM6.
//
// Mirrors the Pydantic source of truth in
//   services/backoffice/app/contracts_numbers.py
// and the shared parity fixture
//   services/backoffice/tests/fixtures/numbers.contract.example.json
//
// Field-for-field with contracts_numbers.py. The drift guard is
// `types.numbers.test.ts`, which validates the SAME committed fixture the
// Python shape test (test_numbers_contract_shape.py) round-trips. Both
// halves move together or a test fails.
//
// `CollateralModel` / `PricingModel` are frozen const unions (not free
// strings) so the N5 new-product form's dropdowns source their options from
// here, not free text.
//
// `TenantProductAvailability.is_active` matches the `tenant_products.is_active`
// column 1:1 (ADR-078 D4) — NOT aliased to `available`.
// ===========================================================================

/** Mirrors contracts_numbers.CollateralModel (products.collateral_model CHECK). */
export const COLLATERAL_MODELS = ['vehicle', 'property', 'none'] as const;
export type CollateralModel = (typeof COLLATERAL_MODELS)[number];

/** Mirrors contracts_numbers.PricingModel (products.pricing_model CHECK). */
export const PRICING_MODELS = ['ltv_band', 'margin'] as const;
export type PricingModel = (typeof PRICING_MODELS)[number];

/**
 * Live reachability/connected badge, re-shaped from Health's MetaNumberStatus.
 * Mirrors contracts_numbers.NumberStatus.
 */
export interface NumberStatus {
  reachable: boolean;
  connected: boolean | null;
  status: string | null;
  display_number: string | null;
  verified_name: string | null;
  reason: string | null;
}

/**
 * One tenant's WhatsApp number wiring (Story N1/N2). `phone_number_id` /
 * `whatsapp_number_display` are null when not yet configured — an explicit
 * "not configured" state, never a blank field. `status` degrades
 * independently of the configured value (N1 AC3). Mirrors
 * contracts_numbers.TenantNumberConfig.
 */
export interface TenantNumberConfig {
  tenant_id: string;
  tenant_name: string;
  phone_number_id: string | null;
  /** E.164, display-only (ADR-069). */
  whatsapp_number_display: string | null;
  status: NumberStatus;
  /** ISO-8601 timestamp; null when never updated. */
  updated_at: string | null;
  updated_by: string | null;
  /**
   * ADR-078 D3 — a fixed, non-null operator-facing notice set ONLY on a
   * write (PUT) response; always null on a GET. Mirrors
   * `app.routes.numbers.PROPAGATION_NOTICE` (locked copy, not interpolated).
   * Added in NUM4 (PR #584) — the SPA renders its own locked copy of the
   * same text on the edit/confirm steps BEFORE a write completes (this
   * field is only populated after), see `NumberEditForm.tsx`.
   */
  propagation_notice: string | null;
}

/**
 * Set/change/clear request body (Story N2). `phone_number_id: null` clears
 * the wiring (D6) — the null case is not a special case, just a value.
 * Mirrors contracts_numbers.SetTenantNumberRequest.
 */
export interface SetTenantNumberRequest {
  phone_number_id: string | null;
}

/** One row of the credit-product catalogue. Mirrors contracts_numbers.ProductConfig. */
export interface ProductConfig {
  product_code: string;
  display_name: string;
  collateral_model: CollateralModel;
  pricing_model: PricingModel;
  /** Catalogue-level retirement flag; distinct from tenant availability. */
  is_active: boolean;
}

/**
 * One tenant × product availability row. `is_active` is named to match the
 * `tenant_products.is_active` column 1:1 (ADR-078 D4) — NOT `available`.
 * Mirrors contracts_numbers.TenantProductAvailability.
 */
export interface TenantProductAvailability {
  tenant_id: string;
  product_code: string;
  is_active: boolean;
}

/**
 * New-product-code request body (Story N5). Fields are plain `string` — NOT
 * the frozen `CollateralModel`/`PricingModel` unions — mirroring
 * contracts_numbers.CreateProductRequest 1:1: the N5 form's `<select>`s
 * constrain the VALUES to the frozen enum (AC2), but the wire type stays a
 * thin pass-through so an out-of-domain value still reaches the BFF's typed
 * 422, never a second client-side enum check that could drift from it.
 */
export interface CreateProductRequest {
  product_code: string;
  display_name: string;
  collateral_model: string;
  pricing_model: string;
}

// --- Runtime guards (used by the views + the drift test) --------------------

export const isCollateralModel = (value: unknown): value is CollateralModel =>
  typeof value === 'string' && (COLLATERAL_MODELS as readonly string[]).includes(value);

export const isPricingModel = (value: unknown): value is PricingModel =>
  typeof value === 'string' && (PRICING_MODELS as readonly string[]).includes(value);
